/**
 * Aurora Customization: This file is necessary to set the order's PO Number as the initial value for the credit PO number
 */
import { useContext, useMemo, useState, useEffect } from 'react';
import { map } from 'lodash';

import { StatusChangeContext } from '@broadleaf/admin-components/dist/oms/contexts';
import type {
  IConfirmReturnRequest,
  IOrder,
  IReturnAuthorization,
  IReturnAuthorizationItem
} from '@broadleaf/admin-components/dist/types/oms';
import { utils } from '@broadleaf/admin-components';

const { RequestUtils } = utils;
const { request } = RequestUtils;

async function getCreditDealerInvoiceNumber(
  order: IOrder,
  request: any,
  contextParams: Record<string, any>
) {
  const response = await request(
    {
      method: 'get',
      uri: `/order/return-operations/${order.id}/next-credit-dealer-invoice`,
      scope: 'RETURN_AUTHORIZATION'
    },
    contextParams
  );

  return response.data;
}

/**
 * Hook that returns the initial form values for the status change form.
 *
 * @returns {{
 *   items: [IConfirmReturnItemRequest]
 * }}
 */
export function useInitialValues(): IConfirmReturnRequest {
  const { parent, order, contextParams } = useContext(StatusChangeContext);
  const [creditDealerInvoiceNumber, setCreditDealerInvoiceNumber] = useState<
    string | undefined
  >(undefined);

  const returnAuth = parent as IReturnAuthorization;
  const getConfirmableQuantity = (item: IReturnAuthorizationItem) => {
    const quantityPendingOrConfirmed = item.confirmations
      .filter(
        confirmation =>
          confirmation.status === 'PENDING' ||
          confirmation.status === 'COMPLETED'
      )
      .map(confirmation => confirmation.quantity)
      .reduce((sum, confirmed) => sum + confirmed, 0);
    return item.quantity - quantityPendingOrConfirmed;
  };

  useEffect(() => {
    if (order && request) {
      getCreditDealerInvoiceNumber(order, request, contextParams).then(
        setCreditDealerInvoiceNumber
      );
    }
  }, [order, request, contextParams]);

  return useMemo(
    () => ({
      returnAuthorizationId: returnAuth.id,
      // @ts-ignore purchaseOrderNumber not declared on IOrder
      purchaseOrderNumber: order.purchaseOrderNumber,
      creditDealerInvoiceNumber: creditDealerInvoiceNumber,
      items: map(returnAuth.items, item => ({
        orderFulfillmentItemId: item.orderFulfillmentItemId,
        quantity: getConfirmableQuantity(item),
        condition: item.expectedCondition,
        refundFulfillmentCharge: item.returnType === 'REFUND',
        overrideTotalToRefund: undefined,
        note: ''
      }))
    }),
    [returnAuth, order, creditDealerInvoiceNumber]
  );
}

export default useInitialValues;
